import * as React from 'react';
import { get } from 'lodash';
import { connect } from 'react-redux';
import { formValueSelector, reduxForm } from 'redux-form';
import { Grid, Row, Col } from 'react-flexbox-grid';
import Divider from 'material-ui/Divider';
import { withState } from 'recompose';

import { ColumnType } from '@inqool/uas-web-core/es/components/controls/Grid/constants';
import Button from '@inqool/uas-web-core/es/components/controls/Button';
import {
  RemoteAutoCompleteField,
  RemoteAutoComplete,
  AutoCompleteField,
  AutoComplete,
  UploadField,
  Upload
} from '@components/form';
import { compose, withHandlers } from 'recompose';
import { TransferGridField, TransferGrid } from '@components/form';
import { operationalWarehouseAction } from './_operationalWarehouseActions';
import { Loader } from '@components/loader';
import { withNotifications } from '@components/hoc/withNotifications';
import { operationalWarehouseUrl, operationalWarehouseUrlParametrized } from './operationalWarehouseGrid';

const FORM_NAME = 'module-operational-warehouse-transfer';

const enhance = compose<any, any>(
  connect(state => {
    return {
      firstFieldValue: formValueSelector(FORM_NAME)(state, 'firstFormField'),
      secondFieldValue: formValueSelector(FORM_NAME)(state, 'secondFormField'),
      items: formValueSelector(FORM_NAME)(state, 'items'),
      item: formValueSelector(FORM_NAME)(state, 'item')
    };
  }),
  withState('loader', 'setLoader', false),
  withNotifications({ operationalWarehouseAction }),
  withHandlers({
    onSubmit:
      ({
        closeItem,
        url,
        successMessage,
        errorMessage,
        setLoader,
        operationalWarehouseActionNotify,
        firstField,
        secondField,
        oneItem,
        refresh
      }) =>
      async ({ firstFormField, secondFormField, items, item, file }) => {
        setLoader(true);
        const ok = await operationalWarehouseActionNotify(url, successMessage, errorMessage, {
          ...(firstField ? { [firstField.name]: firstFormField.id } : {}),
          ...(secondField ? { [secondField.name]: secondFormField.id } : {}),
          ...(oneItem ? { itemId: item.id } : { itemIds: items.map(({ id }) => id) }),
          ...(file && file.id ? { fileId: file.id } : {})
        });
        setLoader(false);

        if (ok) {
          closeItem();
          refresh();
        }
      }
  }),
  reduxForm({ form: FORM_NAME })
);

export const View = ({
  label,
  firstField,
  secondField,
  handleSubmit,
  closeItem,
  firstFieldValue,
  secondFieldValue,
  items,
  item,
  change,
  useSecondFieldAsFilter,
  fileUpload,
  loader,
  filters,
  oneItem,
  filterField
}) => {
  return (
    <form className="layout" onSubmit={handleSubmit}>
      {loader && <Loader />}

      <div className="transfer-form">
        <Grid fluid={true}>
          <Row>
            <Col className="grid-system-margin" />
          </Row>
          <Row>
            <Col xs={12}>
              <h3 style={{ marginTop: 0 }}>{label}</h3>
            </Col>
          </Row>
          {firstField && secondField ? (
            <Row>
              <Col xs={12} md={6} xl={4}>
                <RemoteAutoCompleteField
                  {...firstField}
                  component={RemoteAutoComplete}
                  fullWidth={true}
                  onChange={() => {
                    if (oneItem) {
                      change('item', undefined);
                    } else {
                      change('items', []);
                    }
                  }}
                  id="firstFormField"
                  name="firstFormField"
                />
              </Col>
              <Col xs={12} md={6} xl={4}>
                {secondField.local ? (
                  <AutoCompleteField
                    {...secondField}
                    component={AutoComplete}
                    fullWidth={true}
                    id="secondFormField"
                    name="secondFormField"
                    labelFunction={o => o.label}
                  />
                ) : (
                  <RemoteAutoCompleteField
                    {...secondField}
                    component={RemoteAutoComplete}
                    fullWidth={true}
                    id="secondFormField"
                    name="secondFormField"
                    params={
                      firstField.url === secondField.url && firstFieldValue
                        ? {
                            filter: [
                              {
                                field: 'id',
                                operation: 'NEQ',
                                value: firstFieldValue.id
                              }
                            ]
                          }
                        : undefined
                    }
                  />
                )}
              </Col>
            </Row>
          ) : (
            <></>
          )}
          {oneItem && firstFieldValue ? (
            <Row>
              <Col xs={12}>
                <RemoteAutoCompleteField
                  component={RemoteAutoComplete}
                  fullWidth={true}
                  id="item"
                  name="item"
                  label="Předmět"
                  url={operationalWarehouseUrlParametrized}
                  doPost={true}
                  labelFunction={o => o.name}
                  params={{
                    filter: [
                      ...(filters || []),
                      {
                        field: filterField,
                        operation: 'EQ',
                        value: firstFieldValue.id
                      }
                    ]
                  }}
                />
              </Col>
            </Row>
          ) : (firstFieldValue && secondFieldValue) || (!firstField && !secondField) ? (
            <div className="transfer-grid-wrapper">
              <TransferGridField
                component={TransferGrid}
                name="items"
                url={operationalWarehouseUrl}
                doPost={true}
                filters={[
                  ...(filters || []),
                  ...(firstFieldValue || secondFieldValue
                    ? [
                        {
                          field: filterField,
                          operation: 'EQ',
                          value: useSecondFieldAsFilter ? secondFieldValue.id : firstFieldValue.id
                        }
                      ]
                    : [])
                ]}
                columns={[
                  {
                    label: 'Název',
                    field: 'name',
                    type: ColumnType.TEXT,
                    width: 200
                  },
                  {
                    label: 'Kategorie',
                    field: 'category.name',
                    displayField: 'category',
                    mapper: parent => get(parent, 'name'),
                    type: ColumnType.TEXT,
                    width: 200
                  },
                  {
                    label: 'Poznámka',
                    field: 'note',
                    type: ColumnType.TEXT,
                    width: 300
                  }
                ]}
              />
            </div>
          ) : (
            <></>
          )}

          {fileUpload && (
            <Row>
              <Col xs={12} xl={8}>
                <Row>
                  <Col xs={12} md={3} xl={2}>
                    <h4>Soubor</h4>
                  </Col>
                  <Col xs={12} md={9} xl={10}>
                    <UploadField component={Upload} name="file" />
                  </Col>
                </Row>
                <Row>
                  <Col className="grid-system-margin" />
                </Row>
              </Col>
            </Row>
          )}
        </Grid>
      </div>

      <Divider />

      <Button
        type="submit"
        label="Potvrdit"
        primary={true}
        style={{ margin: '16px 0 16px 16px' }}
        disabled={
          (firstField && !firstFieldValue) || (secondField && !secondFieldValue) || (!(items && items.length) && !item)
        }
      />
      <Button label="Zrušit" style={{ margin: '16px 0 16px 16px' }} onClick={closeItem} />
    </form>
  );
};

export const OperationalWarehouseTransferForm = enhance(View);



// WEBPACK FOOTER //
// ./src/modules/operationalWarehouse/operationalWarehouseTransferForm.tsx